import styles from "./Table.module.css";
import { Dispatch, SetStateAction } from "react";
import { classNames } from "src/utils/classNames";
import { Row } from "./components";
import { Column, RowData } from "./types";
import { SortContext } from "./utils";
import { Stack } from "@mui/material";

interface ITableProps<T extends RowData> {
  columns: Column<T>[];
  data: T[];
  rowOnClick?: (rowData: T) => void;
  activeSortKey?: string;
  setActiveSortKey?: Dispatch<SetStateAction<string>>;
}

const EmptyState = () => (
  <Stack
    alignItems="center"
    justifyContent="center"
    sx={{ height: "100%", color: "#999" }}
  >
    No results found
  </Stack>
);

export const Table = <T extends RowData>({
  columns,
  data,
  rowOnClick,
  activeSortKey = "",
  setActiveSortKey = () => {
    /* noop */
  },
}: ITableProps<T>) => {
  const fillTableRowStyle = {
    gridColumn: `1 / ${columns.length + 1}`,
  };

  const tableLayoutStyle = {
    gridTemplateColumns: columns.map((column) => column.width).join(" "),
  };

  return (
    <SortContext.Provider value={[activeSortKey, setActiveSortKey]}>
      <Stack overflow="hidden" height="100%">
        <table className={styles.table} style={tableLayoutStyle}>
          <thead
            className={styles.subgrid}
            style={{
              ...fillTableRowStyle,
              position: "sticky",
              top: 0,
              background: "#FFFFFF",
              zIndex: 500,
            }}
          >
            <tr className={classNames(styles.headerRow, styles.subgrid)}>
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={styles.tableCell}
                  style={column.style}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          {data.length > 0 && (
            <tbody
              className={styles.subgrid}
              style={{
                gridRow: `2 / span ${data.length}`,
                ...fillTableRowStyle,
              }}
            >
              {data.map((rowData, index) => (
                <Row<T>
                  key={rowData.id}
                  columns={columns}
                  rowData={rowData}
                  gridRow={index + 1}
                  onClick={rowOnClick}
                />
              ))}
            </tbody>
          )}
        </table>
        {data.length === 0 && <EmptyState />}
      </Stack>
    </SortContext.Provider>
  );
};
